import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import logo from "@/assets/logo.webp";

// Lazy-loaded so the Radix dropdown + avatar code is excluded from the main
// bundle for anonymous visitors (the majority on the homepage).
const UserMenu = lazy(() => import("./UserMenu"));

const nav = [
  { to: "/projects", label: "Projects" },
  { to: "/fundraisers", label: "Fundraisers" },
  { to: "/events", label: "Events" },
  { to: "/news", label: "Blogs" },
  { to: "/newsletters", label: "Newsletters" },
  { to: "/about", label: "About" },
  { to: "/get-involved", label: "Get involved" },
  { to: "/contact", label: "Contact" },
];

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const { user, isAdmin, signOut } = useAuth();
  const { permissions } = usePermissions();
  const isStaff = permissions.some((p) => p.can_view || p.can_edit);

  return (
    <header className="sticky top-0 z-40 w-full border-b border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/65">
      <div className="container mx-auto flex h-16 items-center justify-between px-4">
        <Link to="/" className="flex items-center gap-2" aria-label="Raise Sri Lanka home">
          <img src={logo} alt="Raise Sri Lanka" width={140} height={40} className="h-9 w-auto" />
        </Link>

        <nav className="hidden items-center gap-7 md:flex">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-primary ${
                  isActive ? "text-primary" : "text-foreground/70"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 md:flex">
          <Button asChild size="sm" variant="ghost">
            <Link to="/start-a-fundraiser">Start a fundraiser</Link>
          </Button>
          <Button
            asChild
            size="sm"
            className="rounded-full bg-accent px-5 text-accent-foreground hover:bg-accent/90"
          >
            <Link to="/donate">Donate</Link>
          </Button>
          {user ? (
            <Suspense fallback={<div className="ml-1 h-9 w-9 rounded-full bg-muted" aria-hidden />}>
              <UserMenu user={user} isAdmin={isAdmin} signOut={signOut} />
            </Suspense>
          ) : (
            <Button asChild size="sm" variant="outline" className="rounded-full">
              <Link to="/auth">Sign in</Link>
            </Button>
          )}
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-background md:hidden">
          <div className="container mx-auto flex flex-col gap-1 px-4 py-3">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-3 py-2 text-sm font-medium hover:bg-muted"
              >
                {n.label}
              </Link>
            ))}
            <Button asChild className="mt-2 w-full bg-accent text-accent-foreground hover:bg-accent/90">
              <Link to="/donate" onClick={() => setOpen(false)}>
                Donate
              </Link>
            </Button>
            <Button asChild variant="ghost" className="mt-2 w-full justify-start">
              <Link to="/start-a-fundraiser" onClick={() => setOpen(false)}>
                Start a fundraiser
              </Link>
            </Button>
            <div className="mt-2 border-t pt-2">
              {user ? (
                <>
                  {(isAdmin || isStaff) && (
                    <Link to="/admin" onClick={() => setOpen(false)} className="block rounded-md px-3 py-2 text-sm hover:bg-muted">
                      {isAdmin ? "Admin dashboard" : "Volunteer portal"}
                    </Link>
                  )}
                  <button
                    onClick={() => { setOpen(false); signOut(); }}
                    className="block w-full rounded-md px-3 py-2 text-left text-sm hover:bg-muted"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <Link to="/auth" onClick={() => setOpen(false)} className="block rounded-md px-3 py-2 text-sm hover:bg-muted">
                  Sign in
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
